import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useEntitlements, type FeatureKey } from '@/hooks/useEntitlements';
import { deskVoice } from '@/lib/deskSteps';
import { visibleReps, type HabitPersona, type HabitProgress } from '@/lib/deskHabit';

/** The single reader of Desk Setup habit progress. Every surface uses this — none count on their own. */
export function useDeskHabit() {
  const { user } = useAuth();
  const ent = useEntitlements();
  /* The server resolves the persona (three-way); the plan voice is only the first-paint guess. */
  const guess: HabitPersona = deskVoice(ent.can) === 'buyer' ? 'buyer' : 'seller';
  const q = useQuery({
    queryKey: ['desk-habit', user?.id],
    enabled: !!user && !ent.isUnresolved,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_desk_habit_progress' as never, {} as never);
      if (error) throw error;
      return data as unknown as HabitProgress | null;
    },
  });
  const reps = q.data ? visibleReps(q.data, (c) => ent.can(c as FeatureKey)) : [];
  const persona: HabitPersona = q.data?.persona ?? guess;
  return { ...q, progress: q.data ?? null, reps, persona };
}
